import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Descriptions,
  notification,
  Form,
  Space,
  Row,
  Col,
  Input,
  Typography,
  DatePicker,
  Card,
  Skeleton,
  Button,
  Modal,
} from 'antd';

import MESSAGES from '../../../enums/Messages';
import INTERNAL_LINKS from '../../../enums/InternalLinks';
import CustomButton from '../../../components/CustomButton';
import BreadcrumbContainer from '../../../containers/Breadcrumb';
import { useParams, Link, useHistory } from 'react-router-dom';
import {
  get,
  patch,
  getContractors,
  getCurrentDates,
} from '../../../api/module/mass_ctm_date';
import '../Form/style.scss';
import AjaxTable from '../../../components/AjaxTable';
import ContractorsModal from '../MassForm/ContractorsModal';
import { catchException } from '../../../utils/helpers/helpers';
import ModificationModuleSaveButton from '../../../components/ModificationComponents';
import { render } from '@testing-library/react';
import { EyeOutlined } from '@ant-design/icons';

const { Title } = Typography;
const dateFormat = 'YYYY-MM-DD';
const dateFormatText = 'DD MMMM YYYY';

const MassModificationChangeTerm = ({ sendPerm }) => {
  var { modification_id } = useParams();
  const [formModifyDate] = Form.useForm();

  const [projectId, setProjectID] = useState(0);
  const [selectedDate, setSelectedDate] = useState('');

  const navigate = useHistory();

  const breadcrumbItems = [
    {
      text: 'Módulo Modificaciones Contractuales',
      to: `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}`,
    },
    {
      text: 'Formulario de Modificaciones Contractuales',
      to: `${INTERNAL_LINKS.CONTRACT_MASS_MODIFICATIONS}/${projectId}/${modification_id}`,
    },

    {
      text: 'Modificacion modulo plazo',
    },
  ];

  const [loadingFormDate, setLoadingFormDate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataInfo, setDataInfo] = useState();
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isMounted, setIsMounted] = useState(true);

  const [viewDate, setViewDate] = useState();

  const getModuleInfo = async (modification_id) => {
    try {
      const response = await get(modification_id);
      let resInfo = response.data.info ? response.data.info : {};
      let modification = response.data.modificacion
        ? response.data.modificacion
        : {};
      let fechaMod = modification.fecha_fin_contrato
        ? dayjs(modification.fecha_fin_contrato, 'YYYY-MM-DD')
        : null;

      formModifyDate.setFieldsValue({
        modified_date: fechaMod,
        justification: modification.justificacion,
        name_clause: modification.clausula,
      });
      if (modification.justificacion == null) {
        fechaMod = null;
      }
      setSelectedDate(fechaMod);
      setProjectID(resInfo.proyecto_id);
      setDataInfo(resInfo);
      setHasLoaded(true);
      setLoading(false);
    } catch (error) {
      catchException(error);
      setHasLoaded(false);
    }
  };

  useEffect(() => {
    if (modification_id && isMounted) {
      getModuleInfo(modification_id);
    }

    return () => {
      setIsMounted(false);
    };
  }, [modification_id, isMounted]);

  const disabledDate = (current) => {
    let finalDate = dayjs(dataInfo.fecha_fin_proyecto).format('YYYY-MM-DD');
    let startDate = dayjs(dataInfo.fecha_inicio_contrato).format('YYYY-MM-DD');

    return (
      current && (current < dayjs(startDate) || current > dayjs(finalDate))
    );
  };

  const formatDate = (dateVal, strFormat) => {
    var dateFinal = dateVal ? dateVal : '';
    if (dateFinal !== '') {
      dateFinal = dayjs(dateFinal).format(strFormat);
    }
    return dateFinal;
  };

  const onFinish = async (inputs) => {
    setLoadingFormDate(true);
    const date = inputs.modified_date
      ? formatDate(inputs.modified_date, 'YYYY-MM-DD')
      : '';
    const justification = inputs.justification ? inputs.justification : '';
    const clause = inputs.name_clause ? inputs.name_clause : '';

    const data = {
      modified_date: date,
      justification: justification,
      clause: clause,
    };

    try {
      await patch(modification_id, data);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: 'Módulo actualizado correctamente.',
        duration: 2,
      });
      setLoadingFormDate(false);
      setTimeout(() => {
        navigate.push(
          `${INTERNAL_LINKS.CONTRACT_MASS_MODIFICATIONS}/${dataInfo.proyecto_id}/${modification_id}`,
        );
      }, MESSAGES.TIME);
    } catch (error) {
      setLoadingFormDate(false);
      catchException(error);
    }
  };

  const handleDateChange = (date, dateString) => {
    setSelectedDate(dateString);
  };

  const columns = [
    {
      title: 'Fecha inicio',
      dataIndex: 'fecha_inicio',
      render: (text) => formatDate(text, dateFormat),
      align: 'center',
    },
    {
      title: 'Fecha Finalización VIGENTE',
      dataIndex: 'fecha_fin',
      render: (text) => formatDate(text, dateFormat),
      align: 'center',
    },
    {
      title: 'Contratistas',
      dataIndex: 'count',
      align: 'center',
    },
    {
      title: 'Acciones',
      key: 'actions',
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <Button
          onClick={() =>
            setViewDate(formatDate(record.fecha_inicio, dateFormat))
          }
          icon={<EyeOutlined />}
          type="primary"
          htmlType="button"
        />
      ),
    },
  ];

  const contractorColumns = [
    {
      title: 'Contratista',
      dataIndex: 'contratista_nombre',
    },
    {
      title: 'No. Contrato',
      dataIndex: 'contrato_num',
      align: 'center',
    },
  ];

  return (
    <>
      <BreadcrumbContainer items={breadcrumbItems} />
      <div className="box-title">
        <Title className={'text-uppercase'} level={2}>
          MODIFICACIÓN CONTRACTUAL PLAZO
        </Title>
      </div>
      <Row align="middle" gutter={8}>
        <Col flex="auto">
          {dataInfo && (
            <Space direction={'vertical'} style={{ width: '100%' }}>
              <Skeleton loading={loading} active>
                <Descriptions
                  layout="horizontal"
                  column={1}
                  bordered
                  styles={{
                    label: { maxWidth: '8rem' },
                  }}
                  className="fade-in"
                >
                  <Descriptions.Item label="Cargo / Rol">
                    <Row align="middle">
                      <Col flex="auto">{dataInfo.cargo_nombre}</Col>
                      <Col flex="0 0">
                        <ContractorsModal initialValues={dataInfo} />
                      </Col>
                    </Row>
                  </Descriptions.Item>
                  <Descriptions.Item label="Identificador interno de la modificación masiva">
                    {dataInfo.identificador_masivo}
                  </Descriptions.Item>
                </Descriptions>
              </Skeleton>
            </Space>
          )}
          <Space direction={'vertical'} style={{ width: '100%' }}>
            <Form
              form={formModifyDate}
              name="form_modificacion_fecha"
              layout="vertical"
              autoComplete="off"
              onFinish={onFinish}
            >
              <Card
                size={'small'}
                bordered={false}
                title={
                  <Title level={3}>1.Plazo (Disminución o prórroga)</Title>
                }
              >
                {dataInfo && sendPerm && (
                  <Form.Item
                    label="Digite en letras el número de la cláusula del contrato a modificar"
                    name="name_clause"
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el nombre de la cláusula',
                      },
                      {
                        max: 100,
                        message:
                          'El campo debe tener un máximo de 100 caracteres',
                      },
                    ]}
                  >
                    <Input
                      type="text"
                      allowClear
                      placeholder=""
                      name="name_clause"
                    />
                  </Form.Item>
                )}
              </Card>
              {dataInfo ? (
                <>
                  <AjaxTable
                    tableTitle="Listado de fechas vigentes"
                    columns={columns}
                    endpoint={getCurrentDates}
                    query={dataInfo.id}
                    scroll={{ x: false }}
                    rowKey="fecha_inicio"
                  />
                  <Modal
                    width={'80%'}
                    open={viewDate}
                    footer={null}
                    onCancel={() => setViewDate()}
                    title="Contratistas por fecha"
                    destroyOnClose
                  >
                    <AjaxTable
                      columns={contractorColumns}
                      endpoint={getContractors}
                      query={{ id: dataInfo.id, fecha_inicio: viewDate }}
                      scroll={{ x: false }}
                      rowKey="id"
                    />
                  </Modal>
                </>
              ) : (
                <Skeleton loading active />
              )}

              <Space direction={'vertical'} style={{ width: '100%' }}>
                <Card
                  size={'middle'}
                  bordered={false}
                  title={<Title level={4}>Nueva fecha de terminación</Title>}
                  className="fade-in"
                >
                  <Row>
                    <Col span={12}>
                      <Form.Item
                        label="Fecha terminación modificada"
                        name="modified_date"
                        rules={[
                          {
                            required: true,
                            type: 'object',
                          },
                        ]}
                      >
                        {dataInfo && hasLoaded && (
                          <DatePicker
                            allowClear
                            onChange={handleDateChange}
                            format={dateFormat}
                            disabledDate={disabledDate}
                            placeholder="Fecha modificada"
                            style={{
                              width: '100%',
                            }}
                          />
                        )}
                      </Form.Item>
                    </Col>
                    <Col span={12} align="middle">
                      <p
                        style={{
                          textAlign: 'left',
                          paddingTop: '35px',
                          paddingLeft: '10px',
                        }}
                      >
                        {formatDate(selectedDate, dateFormatText)}
                      </p>
                    </Col>
                  </Row>
                  <Form.Item
                    label="Justificación"
                    name="justification"
                    rules={[
                      {
                        required: true,
                        message: 'Ingresa la Justificación',
                      },
                    ]}
                  >
                    <Input.TextArea allowClear />
                  </Form.Item>

                  {hasLoaded && (
                    <Space>
                      <CustomButton
                        htmlType="button"
                        className={'back-button'}
                        type="default"
                        text={
                          <Link
                            to={`${INTERNAL_LINKS.CONTRACT_MASS_MODIFICATIONS}/${dataInfo.proyecto_id}/${modification_id}`}
                          >
                            Volver
                          </Link>
                        }
                      />
                      <ModificationModuleSaveButton
                        loading={loadingFormDate}
                        onConfirm={() => formModifyDate.submit()}
                      />
                    </Space>
                  )}
                </Card>
              </Space>
            </Form>
          </Space>
        </Col>
      </Row>
    </>
  );
};

export default MassModificationChangeTerm;
